(ns practitest.frontend.components.project-settings-tabs
  (:require
    [clojure.string :as str]
    [re-frame.core :as re-frame]
    [practitest.frontend.subs :as subs]
    [practitest.frontend.common.permissions :as permissions]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.horizontal-scroll-strip :as hss]))

(defn- visible-tabs
  "Returns the ordered list of tabs the current user can see."
  [project-id automation-enabled? new-view-editor?]
  (let [proj   (str "/projects/" project-id)
        p      (str "/p/" project-id)
        group? (permissions/has-permission? "users_n_per")]
    (cond-> []
      true
      (conj {:key   :personal
             :label (get-translation "settings_sub_navbar.personal" "Personal")
             :href  "/personals"})

      group?
      (conj {:key   :project
             :label (get-translation "settings_sub_navbar.project" "Project")
             :href  (str p "/edit")})

      (and group? automation-enabled?)
      (conj {:key   :automation
             :label (get-translation "settings_sub_navbar.automation" "Automation")
             :href  (str proj "/automation")})

      group?
      (conj {:key   :integrations
             :label (get-translation "settings_sub_navbar.integrations" "Integrations")
             :href  (str proj "/integration_settings")})

      (not new-view-editor?)
      (conj {:key   :filters
             :label (get-translation "settings_sub_navbar.filters" "Filters")
             :href  (str proj "/custom_views")})

      (permissions/has-permission? "customization")
      (conj {:key   :fields
             :label (get-translation "settings_sub_navbar.fields" "Fields")
             :href  (str proj "/custom_fields")})

      (permissions/has-permission? "runs")
      (conj {:key   :step-params
             :label (get-translation "settings_sub_navbar.step_parameters" "Step Parameters")
             :href  (str proj "/step_params")})

      (permissions/has-permission? "workflow")
      (conj {:key   :workflow
             :label (get-translation "settings_sub_navbar.workflow" "Workflow")
             :href  (str proj "/workflow_editor")})

      (or (permissions/has-permission? "dashboard_edit")
          (permissions/has-permission? "dashboard_private"))
      (conj {:key   :dashboard
             :label (get-translation "settings_sub_navbar.dashboard" "Dashboard")
             :href  (str p "/dashboards")})

      group?
      (conj {:key   :users-groups
             :label (get-translation "settings_sub_navbar.users_and_groups" "Users & Groups")
             :href  (str proj "/groups")})

      (permissions/has-permission? "import_export")
      (conj {:key   :import-export
             :label (get-translation "settings_sub_navbar.import_and_export" "Import & Export")
             :href  (str proj "/import")}))))

(defn- active-tab-key
  "Return the tab key matching the current URL, or nil."
  [project-id]
  (let [path (str/lower-case (dom/get-current-path))
        proj  (str "/projects/" project-id)
        p     (str "/p/" project-id)]
    (cond
      (str/starts-with? path "/personals")                          :personal
      (or (= path (str p "/edit"))
          (= path (str proj "/edit")))                              :project
      (str/starts-with? path (str proj "/automation"))              :automation
      (or (str/starts-with? path (str proj "/integration_settings"))
          (str/starts-with? path (str proj "/integration_setting"))
          (str/starts-with? path (str proj "/issue_integration")))  :integrations
      (or (str/starts-with? path (str proj "/custom_views"))
          (str/starts-with? path (str proj "/folders")))            :filters
      (or (str/starts-with? path (str proj "/custom_fields"))
          (str/starts-with? path (str proj "/system_fields"))
          (str/starts-with? path (str p "/system_fields"))
          (str/starts-with? path (str proj "/sections")))           :fields
      (str/starts-with? path (str proj "/step_params"))             :step-params
      (or (str/starts-with? path (str proj "/workflow_editor"))
          (str/starts-with? path (str proj "/statuses")))           :workflow
      (or (str/starts-with? path (str p "/dashboards"))
          (str/starts-with? path (str p "/dashboard_items")))       :dashboard
      (str/starts-with? path (str proj "/groups"))                  :users-groups
      (str/starts-with? path (str proj "/import"))                  :import-export
      :else nil)))

(defn- settings-tab-strip [tabs active-key is-owner? account-settings-href]
  [:div.project-settings-tab-strip
   [hss/horizontal-scroll-strip {:active-key active-key}
    [:div.tabs-container
     (for [{:keys [key label href]} tabs]
       ^{:key key}
       [:a.tab {:href  href
                :class (when (= key active-key) "active")}
        label])]]
   (when is-owner?
     [buttons/button
      {:tertiary? true
       :class     "project-settings-account-link"
       :icon      [icons/mux-gear-outline]
       :text      (get-translation "settings_sub_navbar.account_settings" "Account Settings")
       :on-click  #(js/window.open account-settings-href "_blank")}])])

(defn- settings-breadcrumb [active-tab breadcrumbs project-href]
  ;; active-tab:  {:key ... :label "..." :href "..."}
  ;; breadcrumbs: vector of {:label "..." :href "..."} — extra levels beyond the active tab
  (let [has-depth?     (seq breadcrumbs)
        last-crumb-idx (dec (count breadcrumbs))]
    [:div.project-settings-breadcrumb
     [:span.project-settings-breadcrumb-current
      (get-translation "settings_sub_navbar.project_settings" "Project Settings")]
     (when (:label active-tab)
       [:<>
        [:span.project-settings-breadcrumb-sep "/"]
        (if has-depth?
          [:a.project-settings-breadcrumb-section {:href (:href active-tab)} (:label active-tab)]
          [:span.project-settings-breadcrumb-current (:label active-tab)])])
     (map-indexed
       (fn [idx {:keys [label href]}]
         ^{:key idx}
         [:<>
          [:span.project-settings-breadcrumb-sep "/"]
          (if (= idx last-crumb-idx)
            [:span.project-settings-breadcrumb-current label]
            (if href
              [:a.project-settings-breadcrumb-section {:href href} label]
              [:span.project-settings-breadcrumb-section label]))])
       breadcrumbs)]))

(defn project-settings-tabs
  "Root wrapper - reads re-frame state and renders tabs + breadcrumb.
   `options` is the attributes map from the webcomponent element."
  [options]
  (let [current-project    @(re-frame/subscribe [::subs/current-project])
        current-account    @(re-frame/subscribe [::subs/current-account])
        user-accounts      @(re-frame/subscribe [::subs/user-accounts])
        app-options        @(re-frame/subscribe [::subs/options])
        project-id         (:id current-project)
        account-id         (:id current-account)
        automation?        (:automation-enabled? app-options)
        new-view-editor?   @(re-frame/subscribe [::subs/new-view-editor-enabled?])
        tabs               (visible-tabs project-id automation? new-view-editor?)
        active-key         (active-tab-key project-id)
        active-tab         (some #(when (= (:key %) active-key) %) tabs)
        breadcrumbs        (or (:breadcrumbs options)
                               (some-> (:data-breadcrumbs options)
                                       js/JSON.parse
                                       (js->clj :keywordize-keys true)))
        current-user-acct  (some #(when (= (:id (:account %)) account-id) %) user-accounts)
        is-owner?          (:is_owner current-user-acct)
        account-href       (str "/accounts/" account-id "/settings")
        project-href       (when (permissions/has-permission? "users_n_per")
                             (str "/p/" project-id "/edit"))]
    [:div.project-settings-tabs-container
     [settings-tab-strip tabs active-key is-owner? account-href]
     [settings-breadcrumb active-tab breadcrumbs project-href]]))
